import React, { useMemo, useCallback } from "react";
import NavigationTabs from "../../distributor-settings/network-access/navigation-tabs";
import { useNavigate, useParams, useLocation, Routes, Route, Navigate } from "react-router-dom";
import { APP_ROUTES, ROUTE_PATHS } from "../../../constants/app.constants";
import { mockDistributorsData } from "../distributors-table/mock.data";
import DistributorHeader from "./distributors-header/distributor-header";
import { MspAccountInfoHeader } from "./msp-account-info-header/msp-account-info-header";
import { MspAccountInfoNav } from "./msp-account-info-nav/msp-account-info-nav";
import DistributorOverview from "./distributor-overview/distributor-overview";
import DistributorMspAccounts from "./distributor-msp-accounts/distributor-msp-accounts";
import DistributorMembers from "./distributor-members/distributor-members";
import DistributorStackReport from "./distributor-stack-report/distributor-stack-report";
import DistributorSettings from "./distributor-settings/distributor-settings";
import LogsTabLabel from "./logs-tab-label/logs-tab-label";
import { MspAccountInfoOverview } from "./msp-account-info-overview/msp-account-info-overview";
import MspAccountAbout from "../../SubscriptionBackendPortal/mspAccountsSubscription/MspAccountAbout";
import MspSupportAccess from "../../SubscriptionBackendPortal/mspAccountsSubscription/MspSupportAccess";
import MembersAccessPage from "../../distributor-settings/members-access";
import StackPage from "../../stack/stack.page";
import StackReportSection from "../../SubscriptionBackendPortal/mspAccountsSubscription/stack-report-section";
import MspSettings from "../../msp-settings";
import IntegrationsPage from "../../integrations";
import SubscriptionDetails from "../../subscription-details";
import "../distributor-account-details/distributor-account-details.scss";

const TabKeys = {
  overview: "overview",
  subscription: "subscription",
  stackReport: "stackReport",
  members: "members",
  settings: "settings",
  mspAccounts: "mspAccounts",
  logs: "logs",
}

const DistributorInfoPages = () => {
  const { distributorId, mspAccountId: mspAccountIdFromParams } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract mspAccountId from pathname if not available from params
  const mspAccountId = useMemo(() => {
    if (mspAccountIdFromParams) return mspAccountIdFromParams;
    
    // Extract from pathname: /subscription-portal/distributors/:distributorId/msp-accounts/:mspAccountId/*
    const match = location.pathname.match(/\/msp-accounts\/([^/]+)\//);
    return match ? match[1] : null;
  }, [mspAccountIdFromParams, location.pathname]);

  // Find distributor data from mock data
  const distributorData = useMemo(() => {
    if (!distributorId) return null;
    return mockDistributorsData.find(d => d.id === distributorId);
  }, [distributorId]);

  // Flag to detect if we're on any MSP account page
  // URL pattern: /subscription-portal/distributors/:distributorId/msp-accounts/:mspAccountId/*
  const isDistributorMspAccountInfo = useMemo(() => {
    if (!distributorId) return false;
    const currentPath = location.pathname;
    // Check if path matches the pattern: /distributors/:distributorId/msp-accounts/:mspAccountId/*
    const pattern = new RegExp(`/subscription-portal/distributors/${distributorId}/msp-accounts/[^/]+/`);
    return pattern.test(currentPath);
  }, [location.pathname, distributorId]);

  const activeTab = useMemo(() => {
    if (!distributorId) return TabKeys.overview;
    const currentPath = location.pathname;
    const routes = ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages;
    
    for (const [tabKey, routeFn] of Object.entries(routes)) {
      if (typeof routeFn !== "function") continue;

      const routePath = routeFn(distributorId);
      if (currentPath.startsWith(routePath)) {
        return tabKey;
      }
    }
    
    return TabKeys.overview;
  }, [location.pathname, distributorId]);

  const getRoutePath = useCallback((routeKey) => {
    if (!distributorId) return '';
    const routeFn = ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages[routeKey];
    return typeof routeFn === 'function' ? routeFn(distributorId) : routeFn;
  }, [distributorId]);

  const navigationTabs = useMemo(() => {
    if (!distributorId) return [];
    return [
      { 
        eventKey: TabKeys.overview, 
        label: "Overview", 
        active: activeTab === TabKeys.overview, 
        onClick: () => navigate(getRoutePath(TabKeys.overview))
      },
      { 
        eventKey: TabKeys.subscription, 
        label: "Subscription", 
        active: activeTab === TabKeys.subscription, 
        onClick: () => navigate(getRoutePath(TabKeys.subscription)),
      },
      { 
        eventKey: TabKeys.stackReport, 
        label: "Stack Report", 
        active: activeTab === TabKeys.stackReport, 
        onClick: () => navigate(getRoutePath(TabKeys.stackReport)),
      },
      {
        eventKey: TabKeys.members, 
        label: "Members", 
        active: activeTab === TabKeys.members, 
        onClick: () => navigate(getRoutePath(TabKeys.members)),
      },
      { 
        eventKey: TabKeys.settings, 
        label: "Settings", 
        active: activeTab === TabKeys.settings, 
        onClick: () => navigate(getRoutePath(TabKeys.settings)),
      },
      { 
        eventKey: TabKeys.mspAccounts, 
        label: "MSP Accounts", 
        active: activeTab === TabKeys.mspAccounts, 
        onClick: () => navigate(getRoutePath(TabKeys.mspAccounts)),
      },
      { 
        eventKey: TabKeys.logs, 
        label: <LogsTabLabel />, 
        active: activeTab === TabKeys.logs, 
        onClick: () => navigate(getRoutePath(TabKeys.logs)),
        disabled: true,
      },
    ];
  }, [activeTab, navigate, getRoutePath, distributorId]);

  if (!distributorId || !distributorData) {
    return (
      <div className="d-flex align-items-center justify-content-center page-loading">
        <div>Distributor not found</div>
      </div>
    );
  }

  return (
    <div className="msp-subscription-details">
      {!isDistributorMspAccountInfo && (
        <DistributorHeader 
          distributorData={distributorData} 
        />
      )}
      {isDistributorMspAccountInfo && (
        <MspAccountInfoHeader 
          isDistributorMspAccountInfo={isDistributorMspAccountInfo}
        />
      )}
      <div className="tabs-containera">
        <div className="h-100 d-flex flex-column flex-1">
          {!isDistributorMspAccountInfo && (
            <div
              sm={12}
              className=" d-flex justify-content-between align-items-center ml- p-0 overflow-x-auto hide-scrollbar"
            >
              <div className="navs-container w-100">
                <NavigationTabs tabs={navigationTabs} />
              </div>
            </div>
          )}
          {isDistributorMspAccountInfo && (
            <div
              sm={12}
              className=" d-flex justify-content-between align-items-center ml- p-0 overflow-x-auto hide-scrollbar"
            >
              <MspAccountInfoNav />
            </div>
          )}
          <div sm={12} className="ml-1 p-0 flex-1 d-flex flex-column">
            <Routes>
              <Route
                path={APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.overview}
                element={<DistributorOverview distributorId={distributorId} />}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.subscription}
                element={<SubscriptionDetails isDistributorView={true} />}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.mspAccounts}
                element={<DistributorMspAccounts />}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.members}
                element={<DistributorMembers />}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.stackReport}
                element={<DistributorStackReport />}
              />
              <Route
                path={`${APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.settings}/*`}
                element={<DistributorSettings isDistributorView={true} />}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.supportAccess}
                element={isDistributorMspAccountInfo ? <MspSupportAccess accountId={mspAccountId} /> : null}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.about}
                element={isDistributorMspAccountInfo ? <MspAccountAbout accountId={mspAccountId} /> : null}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.securityStack}
                element={isDistributorMspAccountInfo ? <StackPage /> : null}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.integrations}
                element={isDistributorMspAccountInfo ? <IntegrationsPage /> : null}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.logs}
                element={isDistributorMspAccountInfo ? <div style={{ padding: "24px 40px" }}>Logs (Coming Soon)</div> : null}
              />
              {/* <Route 
                path="" 
                element={
                  <Navigate 
                    to={
                      isDistributorMspAccountInfo && mspAccountId 
                        ? APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.overview
                        : APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.overview
                    } 
                    replace
                  />
                } 
              />
              <Route 
                path="*" 
                element={
                  <Navigate 
                    to={APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.overview} 
                    replace
                  />
                } 
              
              
              /> */}


<Route
  index
  element={
    <Navigate
      to={distributorId ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.overview(distributorId) : ROUTE_PATHS.subscriptionPortalPages.distributors}
      replace
    />
  }
/>

<Route
  path="*"
  element={
    <Navigate
      to={distributorId ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.overview(distributorId) : ROUTE_PATHS.subscriptionPortalPages.distributors}
      replace
    />
  }
/>
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistributorInfoPages;
